/**
 * 여러 과압 시나리오를 비교해 **지배 케이스(governing case)** 를 정하고
 * 그 소요 면적으로 오리피스를 선정한다.
 *
 * 이 모듈이 앱의 뼈대다. 개별 사이징식은 각 모듈이 담당하고,
 * 여기서는 "어느 시나리오가 밸브 크기를 결정하는가"만 다룬다.
 *
 * ── 왜 유량이 아니라 면적으로 비교하는가 ─────────────────────
 * 허용 과압이 상황마다 다르기 때문이다(API 520 Part I 5.4).
 *   - 운전 상황, 단일 설치 : 축적압 ≤ MAWP 110 % → 과압 10 %
 *   - 운전 상황, 복수 설치 : 축적압 ≤ MAWP 116 % → 과압 16 %
 *   - 화재 상황(단일·복수·보조 모두) : 축적압 ≤ MAWP 121 % → 과압 21 %
 * 과압이 다르면 배출 압력 P1 이 달라지고, 같은 유량이라도 소요 면적이 달라진다.
 * 화재 케이스는 유량이 가장 크더라도 P1 이 높아 면적은 작을 수 있다.
 * **따라서 반드시 소요 면적으로 비교해야 한다.**
 */
import {
  relievingPressureKpaAbs,
  sizeGasOrVapor,
  type GasSizingInput,
  type GasSizingSuccess,
} from "./gas";
import {
  sizeSteam,
  type SteamSizingInput,
  type SteamSizingSuccess,
} from "./steam";
import {
  sizeLiquid,
  type LiquidSizingInput,
  type LiquidSizingSuccess,
} from "./liquid";
import {
  sizeTwoPhase,
  type TwoPhaseSizingInput,
  type TwoPhaseSizingSuccess,
} from "./twoPhase";
import {
  sizeSubcooledLiquid,
  type SubcooledSizingInput,
  type SubcooledSizingSuccess,
} from "./subcooledLiquid";
import {
  selectOrificeWithParallel,
  type OrificeSelectionResult,
} from "./orifice";

/** 과압 상황 구분. 허용 과압이 달라진다 (5.4.2 / 5.4.3). */
export type ContingencyKind = "operating" | "fire";
/** 설치 형태. 운전 상황에서만 허용 과압에 영향을 준다. */
export type InstallationKind = "single" | "multiple";

/**
 * 허용 과압(%) — API 520 Part I 5.4.2.1.1 · 5.4.2.2 · 5.4.3.1.1.
 * ASME Code 의 축적압 한계(110 / 116 / 121 % of MAWP)에서 나온다.
 */
export function allowableOverpressurePercent(
  contingency: ContingencyKind,
  installation: InstallationKind,
): number {
  if (contingency === "fire") return 21;
  return installation === "multiple" ? 16 : 10;
}

/** 유체 상태별 입력. 배출 압력은 케이스 공통값에서 주입하므로 여기서 뺀다. */
export type FluidSpec =
  | ({ readonly kind: "gas" } & Omit<
      GasSizingInput,
      "relievingPressureKpaAbs"
    >)
  | ({ readonly kind: "steam" } & Omit<
      SteamSizingInput,
      "relievingPressureKpaAbs"
    >)
  | ({ readonly kind: "liquid" } & (
      | (Omit<
          Extract<LiquidSizingInput, { certification: "certified" }>,
          "relievingPressureKpaGauge"
        > & { readonly certification: "certified" })
      | (Omit<
          Extract<LiquidSizingInput, { certification: "non-certified" }>,
          "setPressureKpaGauge"
        > & { readonly certification: "non-certified" })
    ))
  | ({ readonly kind: "two-phase" } & Omit<
      TwoPhaseSizingInput,
      "relievingPressureKpaAbs"
    >)
  | ({ readonly kind: "subcooled" } & (
      | (Omit<
          Extract<SubcooledSizingInput, { certification: "certified" }>,
          "relievingPressureKpaAbs"
        > & { readonly certification: "certified" })
      | (Omit<
          Extract<SubcooledSizingInput, { certification: "non-certified" }>,
          | "relievingPressureKpaAbs"
          | "relievingPressureKpaGauge"
          | "setPressureKpaGauge"
        > & { readonly certification: "non-certified" })
    ));

/** 검토 대상 시나리오 하나. */
export interface ScenarioEntry {
  /** 화면에서 행을 구분하는 값. 계산에는 쓰이지 않는다. */
  readonly id: string;
  /** API 521 Table 1 항목 번호 1~17 */
  readonly item: number;
  /** 표시용 이름 (하위 항목 포함) */
  readonly label: string;
  /**
   * 설계자가 직접 쓰는 과압 상황 설명.
   * 표준 항목명만으로는 "이 설비에서 구체적으로 어떤 상황인지"가 남지 않는다.
   * 계산서·PSM 명세서에 그대로 옮길 근거가 되므로 계산에는 쓰지 않되 항상 보존한다.
   */
  readonly description: string;
  readonly contingency: ContingencyKind;
  readonly fluid: FluidSpec;
  /** 체크 해제하면 비교에서 제외한다(입력은 남긴다). */
  readonly included: boolean;
}

/** 소요 분출용량과 그 단위. 유체 상태에 따라 질량유량/체적유량이 갈린다. */
export interface ScenarioFlow {
  readonly value: number;
  readonly unit: "kg/h" | "L/min";
  /**
   * 질량유량 (kg/h) — 유체 상태와 무관하게 항상 채운다.
   *
   * PSM 명세서는 질량유량 기준을 요구하는 경우가 많다. 액체·과냉 액체는
   * 사이징 자체는 체적유량(L/min, API 520 Part I 식 (33)/(43)이 요구하는
   * 단위)으로 하되, 이 필드로 kg/h 환산값을 별도로 제공한다.
   *   질량유량 = 체적유량(L/min) × 60(→ m³/h) ÷ 1000 × 밀도(kg/m³)
   */
  readonly massFlowKgH: number;
}

/** 유체 입력에서 소요 분출용량을 꺼낸다 (PSM 명세서 기재용). */
export function scenarioFlow(fluid: FluidSpec): ScenarioFlow {
  switch (fluid.kind) {
    case "gas":
    case "steam":
    case "two-phase":
      return {
        value: fluid.requiredFlowKgH,
        unit: "kg/h",
        massFlowKgH: fluid.requiredFlowKgH,
      };
    case "liquid":
      return {
        value: fluid.requiredFlowLpm,
        unit: "L/min",
        // L/min × 60 = L/h, ÷1000 = m³/h, × (SG×1000 kg/m³) = kg/h
        // → L/min × 60 × SG 로 정리된다.
        massFlowKgH: fluid.requiredFlowLpm * 60 * fluid.specificGravity,
      };
    case "subcooled":
      return {
        value: fluid.volumetricFlowLpm,
        unit: "L/min",
        massFlowKgH:
          fluid.volumetricFlowLpm * 0.06 * fluid.inletLiquidDensityKgM3,
      };
  }
}

/** 밸브 하나에 대한 검토 전체. */
export interface PsvCase {
  readonly tag: string;
  /** 설정압 (kPag). 단일 설치에서는 MAWP 이하여야 한다 (5.4.2.1.1). */
  readonly setPressureKpaGauge: number;
  readonly installation: InstallationKind;
  readonly scenarios: readonly ScenarioEntry[];
}

/**
 * 사이징 모듈이 돌려준 결과 원본.
 * 계산서에 중간값(계수·판정·적용 식)을 그대로 옮기기 위해 보관한다.
 * 계산 실패면 null.
 */
export type SizingDetail =
  | {
      readonly kind: "gas";
      readonly input: GasSizingInput;
      readonly result: GasSizingSuccess;
    }
  | {
      readonly kind: "steam";
      readonly input: SteamSizingInput;
      readonly result: SteamSizingSuccess;
    }
  | {
      readonly kind: "liquid";
      readonly input: LiquidSizingInput;
      readonly result: LiquidSizingSuccess;
    }
  | {
      readonly kind: "two-phase";
      readonly input: TwoPhaseSizingInput;
      readonly result: TwoPhaseSizingSuccess;
    }
  | {
      readonly kind: "subcooled";
      readonly input: SubcooledSizingInput;
      readonly result: SubcooledSizingSuccess;
    };

export interface EvaluatedScenario {
  readonly entry: ScenarioEntry;
  /** 소요 분출용량 (PSM 명세서 기재용). 계산 성공 여부와 무관하게 채운다. */
  readonly flow: ScenarioFlow;
  /** 계산 결과 원본. 계산서 중간값 기재용. 실패면 null. */
  readonly detail: SizingDetail | null;
  /** 적용된 허용 과압 (%) */
  readonly overpressurePercent: number;
  /** 배출 압력 (kPa 절대) */
  readonly relievingPressureKpaAbs: number;
  /** 계산 성공 시 소요 면적 (mm²). 실패면 null. */
  readonly requiredAreaMm2: number | null;
  /** 적용된 표준 식 */
  readonly equation: string | null;
  readonly warnings: readonly string[];
  /** 실패 사유. 성공이면 null. */
  readonly error: string | null;
}

export interface GoverningCaseResult {
  /** 면적 내림차순. 계산 실패 항목은 뒤로 보낸다. */
  readonly ranked: readonly EvaluatedScenario[];
  /** 지배 케이스. 계산에 성공한 시나리오가 하나도 없으면 null. */
  readonly governing: EvaluatedScenario | null;
  /** 지배 케이스 면적으로 선정한 오리피스. 지배 케이스가 없으면 null. */
  readonly selection: OrificeSelectionResult | null;
  /** 계산에 실패한 시나리오 수 */
  readonly failedCount: number;
  /** 검토에서 제외된 시나리오 수 */
  readonly excludedCount: number;
}

/** 하나의 시나리오를 케이스 공통 조건과 합쳐 계산한다. */
export function evaluateScenario(
  psvCase: PsvCase,
  entry: ScenarioEntry,
): EvaluatedScenario {
  const overpressurePercent = allowableOverpressurePercent(
    entry.contingency,
    psvCase.installation,
  );
  const p1Abs = relievingPressureKpaAbs(
    psvCase.setPressureKpaGauge,
    overpressurePercent,
  );
  const p1Gauge =
    psvCase.setPressureKpaGauge * (1 + overpressurePercent / 100);

  const base = {
    entry,
    flow: scenarioFlow(entry.fluid),
    overpressurePercent,
    relievingPressureKpaAbs: p1Abs,
  };

  const failed = (message: string): EvaluatedScenario => ({
    ...base,
    detail: null,
    requiredAreaMm2: null,
    equation: null,
    warnings: [],
    error: message,
  });

  const fluid = entry.fluid;

  switch (fluid.kind) {
    case "gas": {
      const { kind: _k, ...rest } = fluid;
      const input: GasSizingInput = {
        ...rest,
        relievingPressureKpaAbs: p1Abs,
      };
      const r = sizeGasOrVapor(input);
      if (!r.ok) return failed(r.message);
      return {
        ...base,
        detail: { kind: "gas", input, result: r },
        requiredAreaMm2: r.requiredAreaMm2,
        equation: r.equation,
        warnings: r.warnings,
        error: null,
      };
    }
    case "steam": {
      const { kind: _k, ...rest } = fluid;
      const input: SteamSizingInput = {
        ...rest,
        relievingPressureKpaAbs: p1Abs,
      };
      const r = sizeSteam(input);
      if (!r.ok) return failed(r.message);
      return {
        ...base,
        detail: { kind: "steam", input, result: r },
        requiredAreaMm2: r.requiredAreaMm2,
        equation: r.equation,
        warnings: r.warnings,
        error: null,
      };
    }
    case "liquid": {
      const { kind: _k, ...rest } = fluid;
      // 액체 식은 게이지압을 쓴다 (5.8 / 5.9).
      const input: LiquidSizingInput =
        rest.certification === "certified"
          ? { ...rest, relievingPressureKpaGauge: p1Gauge }
          : { ...rest, setPressureKpaGauge: psvCase.setPressureKpaGauge };
      const r = sizeLiquid(input);
      if (!r.ok) return failed(r.message);
      return {
        ...base,
        detail: { kind: "liquid", input, result: r },
        requiredAreaMm2: r.requiredAreaMm2,
        equation: r.equation,
        warnings: r.warnings,
        error: null,
      };
    }
    case "two-phase": {
      const { kind: _k, ...rest } = fluid;
      const input: TwoPhaseSizingInput = {
        ...rest,
        relievingPressureKpaAbs: p1Abs,
      };
      const r = sizeTwoPhase(input);
      if (!r.ok) return failed(r.message);
      return {
        ...base,
        detail: { kind: "two-phase", input, result: r },
        requiredAreaMm2: r.requiredAreaMm2,
        equation: r.equation,
        warnings: r.warnings,
        error: null,
      };
    }
    case "subcooled": {
      const { kind: _k, ...rest } = fluid;
      const input: SubcooledSizingInput =
        rest.certification === "certified"
          ? { ...rest, relievingPressureKpaAbs: p1Abs }
          : {
              ...rest,
              relievingPressureKpaAbs: p1Abs,
              relievingPressureKpaGauge: p1Gauge,
              setPressureKpaGauge: psvCase.setPressureKpaGauge,
            };
      const r = sizeSubcooledLiquid(input);
      if (!r.ok) return failed(r.message);
      return {
        ...base,
        detail: { kind: "subcooled", input, result: r },
        requiredAreaMm2: r.requiredAreaMm2,
        equation: r.equation,
        warnings: r.warnings,
        error: null,
      };
    }
  }
}

/**
 * 모든 시나리오를 계산해 지배 케이스를 정하고 오리피스를 선정한다.
 *
 * 지배 케이스 = **소요 면적이 가장 큰** 시나리오. 유량이 아니다(모듈 상단 설명 참조).
 * 동률이면 먼저 입력된 쪽을 택한다 — 순서를 바꿔도 결과가 흔들리지 않게 하려는 것이다.
 */
export function findGoverningCase(psvCase: PsvCase): GoverningCaseResult {
  const included = psvCase.scenarios.filter((s) => s.included);
  const excludedCount = psvCase.scenarios.length - included.length;

  const evaluated = included.map((s) => evaluateScenario(psvCase, s));

  const ranked = [...evaluated].sort((a, b) => {
    if (a.requiredAreaMm2 === null && b.requiredAreaMm2 === null) return 0;
    if (a.requiredAreaMm2 === null) return 1; // 실패는 뒤로
    if (b.requiredAreaMm2 === null) return -1;
    return b.requiredAreaMm2 - a.requiredAreaMm2;
  });

  const failedCount = evaluated.filter((e) => e.requiredAreaMm2 === null).length;

  // 동률일 때 입력 순서를 유지하려고 sort 결과가 아니라 원본 순서에서 최대를 찾는다.
  let governing: EvaluatedScenario | null = null;
  for (const e of evaluated) {
    if (e.requiredAreaMm2 === null) continue;
    if (governing === null || e.requiredAreaMm2 > governing.requiredAreaMm2!) {
      governing = e;
    }
  }

  return {
    ranked,
    governing,
    selection:
      governing === null
        ? null
        : selectOrificeWithParallel(governing.requiredAreaMm2!),
    failedCount,
    excludedCount,
  };
}

/* ──────────────────────────────────────────────────────────────
 * 여러 밸브 × 17개 시나리오 요약 매트릭스
 * 열 = PSV 태그, 행 = API 521 Table 1 항목 1~17
 * ────────────────────────────────────────────────────────────── */

export type SummaryCellKind = "na" | "excluded" | "error" | "value";

export interface SummaryCell {
  readonly kind: SummaryCellKind;
  /** 소요 분출용량. 해당 없음이면 null. */
  readonly flow: ScenarioFlow | null;
  /** 소요 면적 (mm²). 계산 실패·해당 없음이면 null. */
  readonly requiredAreaMm2: number | null;
  /** 이 밸브의 지배 케이스인가 */
  readonly isGoverning: boolean;
  /** 설계자가 적은 상황 설명 */
  readonly description: string;
  /** 계산 실패 사유 등 */
  readonly note: string | null;
  /** 같은 항목에 시나리오가 여러 건이면 그 수. 1이면 단일. */
  readonly count: number;
}

export interface SummaryColumn {
  readonly psvCase: PsvCase;
  readonly result: GoverningCaseResult;
}

export interface SummaryRow {
  readonly item: number;
  readonly cells: readonly SummaryCell[];
}

export interface SummaryMatrix {
  readonly columns: readonly SummaryColumn[];
  readonly rows: readonly SummaryRow[];
}

const NA_CELL: SummaryCell = {
  kind: "na",
  flow: null,
  requiredAreaMm2: null,
  isGoverning: false,
  description: "",
  note: null,
  count: 0,
};

/**
 * 여러 밸브를 나란히 놓고 항목별 소요 분출용량을 비교하는 표를 만든다.
 *
 * 행은 API 521 Table 1 의 1~17 을 **항상 전부** 만든다. 그 밸브에
 * 해당 시나리오가 없으면 `na` 로 표시해, 검토에서 빠뜨린 것인지
 * 애초에 해당 없는 것인지 한눈에 드러나게 하려는 것이다.
 *
 * 같은 항목에 시나리오가 여러 건이면 **소요 면적이 큰 쪽**을 대표로 싣고
 * `count` 에 건수를 남긴다.
 */
export function buildSummaryMatrix(
  cases: readonly PsvCase[],
  items: readonly number[],
): SummaryMatrix {
  const columns: SummaryColumn[] = cases.map((c) => ({
    psvCase: c,
    result: findGoverningCase(c),
  }));

  const rows: SummaryRow[] = items.map((item) => ({
    item,
    cells: columns.map((col): SummaryCell => {
      const entries = col.psvCase.scenarios.filter((s) => s.item === item);
      if (entries.length === 0) return NA_CELL;

      const evaluated = entries.map((e) =>
        evaluateScenario(col.psvCase, e),
      );

      // 대표는 소요 면적이 가장 큰 것. 전부 실패면 첫 번째.
      const withArea = evaluated.filter((e) => e.requiredAreaMm2 !== null);
      const rep =
        withArea.length > 0
          ? withArea.reduce((a, b) =>
              b.requiredAreaMm2! > a.requiredAreaMm2! ? b : a,
            )
          : evaluated[0]!;

      const isGoverning =
        col.result.governing !== null &&
        col.result.governing.entry.id === rep.entry.id;

      if (!rep.entry.included) {
        return {
          kind: "excluded",
          flow: rep.flow,
          requiredAreaMm2: rep.requiredAreaMm2,
          isGoverning: false,
          description: rep.entry.description,
          note: "검토 제외",
          count: entries.length,
        };
      }

      if (rep.error !== null) {
        return {
          kind: "error",
          flow: rep.flow,
          requiredAreaMm2: null,
          isGoverning: false,
          description: rep.entry.description,
          note: rep.error,
          count: entries.length,
        };
      }

      return {
        kind: "value",
        flow: rep.flow,
        requiredAreaMm2: rep.requiredAreaMm2,
        isGoverning,
        description: rep.entry.description,
        note: null,
        count: entries.length,
      };
    }),
  }));

  return { columns, rows };
}
